import { Component, OnInit, ViewChild, ElementRef, AfterViewInit } from '@angular/core';
import { Validators, UntypedFormBuilder, AbstractControl, ValidationErrors } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { PasswordValidators } from '../../validators/password.validator';
import { Store } from '@ngrx/store';
import * as fromApp from './../../store/app.reducer';
import * as AuthActions from './../store/auth.actions';
import { customEmailValidator } from '../../shared/validators/email.validator';
import { HttpClient } from '@angular/common/http';
import { SnackbarService } from '../../shared/snackbar/snackbar.service';
import { environment } from '../../../environments/environment';
import { MsalService } from '@azure/msal-angular';
import { firstValueFrom } from 'rxjs';
import { filter, take, map } from 'rxjs/operators';
import { AuthenticationResult } from '@azure/msal-browser';
import { CommonService } from 'src/app/services/common.service';
import { EmployeeModel } from '../../models/employee.model';
import { CustomerModel } from '../../models/customer.model';
declare const google: any;
@Component({
    selector: 'app-register',
    templateUrl: './register.component.html',
    styleUrls: ['./register.component.scss'],
    standalone: false,
})
export class RegisterComponent implements OnInit, AfterViewInit {
    @ViewChild('registerVideo') registerVideo!: ElementRef<HTMLVideoElement>;
    @ViewChild('googleSignInButton') googleSignInButton!: ElementRef;
    public showPasswords: boolean = false;
    public showReTypePasswords: boolean = false;
    public formSubmitted: boolean = false;
    public isVideoLoaded: boolean = false;
    public msalInitialized: boolean = false;
    public isLoading: boolean = false;
    public registerForm = this.formBuilder.group({
        email: ['', [Validators.required, customEmailValidator()]],
        password: ['', [Validators.required, Validators.minLength(8), PasswordValidators.complexityValidator]],
        confirmPassword: ['', [Validators.required]]
    }, {
        validators: this.passwordMatchValidator
    });

    constructor(
        private router: Router,
        private formBuilder: UntypedFormBuilder,
        private translate: TranslateService,
        private store: Store<fromApp.AppState>,
        private http: HttpClient,
        private snackbarService: SnackbarService,
        private msalService: MsalService,
        private service: CommonService,
    ) { }

    async ngOnInit(): Promise<void> {
        this.setupFormValidation();
        try {
            sessionStorage.clear();
            localStorage.clear();
            await this.msalService.instance.initialize();
            this.msalInitialized = true;
        } catch (error) {
            console.error('Failed to initialize MSAL during component initialization', error);
        }
    }

    ngAfterViewInit(): void {
        google?.accounts?.id?.initialize({
            client_id: environment.googleClientId,
            callback: this.handleGoogleSignUp.bind(this),
            cancel_on_tap_outside: true,
            ux_mode: 'popup'
        });

        const video = this.registerVideo.nativeElement;
        video.muted = true;
        video.play().catch(err => {
            console.warn('Autoplay blocked:', err);
        });
    }

    // Form Control Getters
    get email() {
        return this.registerForm.get('email');
    }

    get password() {
        return this.registerForm.get('password');
    }

    get confirmPassword() {
        return this.registerForm.get('confirmPassword');
    }

    // Form Validation Helpers
    private setupFormValidation(): void {
        this.registerForm.valueChanges.subscribe(() => {
            if (this.formSubmitted) {
                this.formSubmitted = false;
            }
            if (this.formSubmitted) {
                this.validateForm();
            }
        });
    }

    private validateForm(): void {
        if (this.registerForm.valid) {
            this.formSubmitted = true;
            return;
        }
    }

    public shouldShowError(controlName: string): boolean {
        const control = this.registerForm.get(controlName);
        return Boolean((control?.touched || this.formSubmitted) && control?.errors);
    }

    public getErrorMessage(controlName: string): string {
        const control = this.registerForm.get(controlName);
        if (!control || !control.errors) return '';

        const errors = control.errors;
        const controlNameCapitalized = controlName.charAt(0).toUpperCase() + controlName.slice(1);

        if (errors['required']) {
            return `${controlNameCapitalized} ${this.translate.instant('form.validation.required')}`;
        }
        if (errors['email']) {
            return this.translate.instant('form.validation.email');
        }
        if (errors['minlength']) {
            return `${controlNameCapitalized} ${this.translate.instant('form.validation.min-length', { minLength: errors['minlength'].requiredLength })}`;
        }
        if (errors['passwordMismatch']) {
            return this.translate.instant('form.validation.passwords-not-match');
        }
        if (errors['passwordComplexity']) {
            const missing = errors['passwordComplexity'].missing;
            const requirements = [];

            if (missing.upperCase) requirements.push(this.translate.instant('form.validation.uppercase-letter'));
            if (missing.lowerCase) requirements.push(this.translate.instant('form.validation.lowercase-letter'));
            if (missing.numeric) requirements.push(this.translate.instant('form.validation.number'));
            if (missing.specialChar) requirements.push(this.translate.instant('form.validation.special-character'));

            return `${controlNameCapitalized} ${this.translate.instant('form.validation.must-include')}: ${requirements.join(', ')}`;
        }
        return '';
    }

    // Password Visibility Helpers
    public togglePasswordVisibility(field: 'password' | 'confirmPassword'): void {
        if (field === 'password') {
            this.showPasswords = !this.showPasswords;
        } else {
            this.showReTypePasswords = !this.showReTypePasswords;
        }
    }

    public getPasswordFieldType(field: 'password' | 'confirmPassword'): string {
        return field === 'password' ?
            (this.showPasswords ? 'text' : 'password') :
            (this.showReTypePasswords ? 'text' : 'password');
    }

    // Form Submission Helper
    public onSubmit(): void {
        window.scrollTo({ top: 0, behavior: 'smooth' });
        this.formSubmitted = true;
        if (this.isLoading) return;

        this.isLoading = true;
        if (this.registerForm.valid) {
            const formData = {
                email: this.email.value,
                password: this.password.value,
                confirmPassword: this.confirmPassword.value
            };

            this.store.dispatch(new AuthActions.RegisterStart(formData));
            this.store.select(state => state.auth)
                .pipe(
                    filter(authState => !!authState.employee),
                    take(1)
                )
                .subscribe(authState => {
                    this.isLoading = false;
                    // OLD FLOW (kept as requested):
                    // if (authState.employee.customers && authState.employee.customers.length > 0) {
                    //     this.router.navigate(['/components/assignments']);
                    // } else {
                    //     this.router.navigate(['/components/on-boarding']);
                    // }

                    // NEW FLOW:
                    // Navigation is handled in auth.effects.ts (register flow + auto company creation).
                });
        } else {
            Object.keys(this.registerForm.controls).forEach(key => {
                const control = this.registerForm.get(key);
                control?.markAsTouched();
            });
            this.isLoading = false;
        }
    }

    private passwordMatchValidator(control: AbstractControl): ValidationErrors | null {
        const password = control.get('password');
        const confirmPassword = control.get('confirmPassword');

        if (password && confirmPassword && password.value !== confirmPassword.value) {
            confirmPassword.setErrors({ passwordMismatch: true });
            return { passwordMismatch: true };
        }

        return null;
    }

    public signUpWithGoogle(): void {
        this.isLoading = true;
        const buttonDiv = document.createElement('div');
        google.accounts.id.renderButton(buttonDiv, {
            type: 'standard',
            theme: 'outline',
            size: 'large',
            text: 'signup_with',
            shape: 'rectangular',
        });

        const googleButton = buttonDiv.querySelector('.nsm7Bb-HzV7m-LgbsSe');
        if (googleButton) {
            (googleButton as HTMLElement).click();
        } else {
            google.accounts.id.prompt((notification: any) => {
                const isNotDisplayed = notification.isNotDisplayed?.() || notification.getNotDisplayedReason?.();
                const isSkipped = notification.isSkippedMoment?.() || notification.getSkippedReason?.();

                if (isNotDisplayed || isSkipped) {
                    this.isLoading = false;
                }
            });
        }

        setTimeout(() => {
            this.isLoading = false;
        }, 5000);
    }

    handleGoogleSignUp(response: any) {
        if (!response || !response.credential) {
            // Means user rejected or closed the popup
            this.isLoading = false;
            return;
        }
        this.http.post(`${environment.apiUrl}/auth/google/callback`, { token: response.credential })
            .subscribe({
                next: (res: any) => {
                    this.isLoading = false;
                    localStorage.setItem('jwt', res.accessToken);
                    localStorage.setItem('authProvider', 'google');
                    if (res.user) {
                        localStorage.setItem('locale', res.user.locale);
                        this.translate.setDefaultLang(res.user.locale);
                        this.translate.use(res.user.locale);
                    }
                    this.store.dispatch(new AuthActions.Autologin());

                    this.store.select(state => state.auth)
                        .pipe(
                            filter(authState => !!authState.employee),
                            take(1)
                        )
                        .subscribe((authState: any) => {
                            this.snackbarService.showSuccess('auth.register.register-success');
                            this.navigateAfterSocialLogin(authState.employee);
                        });
                },
                error: (err) => {
                    this.isLoading = false;
                    console.error('Google login failed', err);
                    this.snackbarService.showError(err.error?.error || 'Authentication failed');
                }
            });
    }

    public async signUpWithMicrosoft(): Promise<void> {
        if (this.isLoading) return;

        this.isLoading = true;
        try {
            const loginResult: AuthenticationResult = await this.msalService.instance.loginPopup({
                scopes: ['user.read', 'openid', 'profile', 'email'],
                prompt: 'select_account'
            });

            if (loginResult) {
                this.msalService.instance.setActiveAccount(loginResult.account);

                let tokenResult: AuthenticationResult;

                try {
                    tokenResult = await firstValueFrom(this.msalService.acquireTokenSilent({
                        scopes: ['user.read'],
                        account: loginResult.account!
                    }));
                } catch (e) {
                    tokenResult = await firstValueFrom(this.msalService.acquireTokenPopup({
                        scopes: ['user.read']
                    }));
                }

                if (!tokenResult) {
                    throw new Error('Failed to acquire token');
                }

                const response = await firstValueFrom(this.http.post(`${environment.apiUrl}/auth/microsoft/token`, {
                    token: tokenResult.accessToken
                }));

                const res: any = response;
                localStorage.setItem('jwt', res.token.accessToken);
                localStorage.setItem('authProvider', 'microsoft');
                if (res.user) {
                    localStorage.setItem('locale', res.user.locale);
                    this.translate.setDefaultLang(res.user.locale);
                    this.translate.use(res.user.locale);
                }

                this.store.dispatch(new AuthActions.Autologin());

                // Wait for auth state to be updated
                await firstValueFrom(
                    this.store.select(state => state.auth).pipe(
                        filter(authState => !!authState.employee),
                        take(1),
                        map(authState => {
                            this.isLoading = false;
                            this.snackbarService.showSuccess('auth.register.register-success');
                            this.navigateAfterSocialLogin(authState.employee);
                        })
                    )
                );
            }
        } catch (err: any) {
            console.error('Microsoft login failed', err);
            this.snackbarService.showError(err.message || 'Authentication failed');
        } finally {
            this.isLoading = false;
        }
    }

    private navigateAfterSocialLogin(employee: EmployeeModel): void {
        if (employee?.customers?.length > 0) {
            this.router.navigate(['/components/assignments']);
            return;
        }
        this.createDefaultCustomerAndNavigateToAssignments(employee);
    }

    private createDefaultCustomerAndNavigateToAssignments(employee: EmployeeModel): void {
        const formData = this.buildDefaultCustomerFormData(employee);
        this.http.post<{ data: CustomerModel }>(`${environment.apiUrl}/customers`, formData).subscribe({
            next: (res) => {
                localStorage.setItem('customer', res.data.id.toString());
                this.store.dispatch(new AuthActions.CreateCustomerSuccess({ customer: res.data }));
                this.store.dispatch(new AuthActions.SwitchCompanyAccount({ customer: res.data }));
                this.router.navigate(['/components/assignments']);
            },
            error: (err) => {
                this.snackbarService.showError(err.error?.error || 'Could not auto create company');
                this.router.navigate(['/components/assignments']);
            }
        });
    }

    private buildDefaultCustomerFormData(employee: EmployeeModel): FormData {
        const formData = new FormData();
        const defaultCompanyName = this.getDefaultCompanyName(employee);
        const contactPerson = `${employee?.firstName || ''} ${employee?.lastName || ''}`.trim() || defaultCompanyName;

        formData.append('idNumber', this.getGeneratedOrganisationNumber());
        formData.append('companyName', defaultCompanyName);
        formData.append('contactPerson', contactPerson);
        formData.append('email', employee?.email || '');
        formData.append('phone', employee?.phone || '');
        formData.append('address', '');
        formData.append('city', '');
        formData.append('biography', '');
        formData.append('customerWebsite', '');
        formData.append('is_auto_created_from_registration', 'true');
        return formData;
    }

    private getDefaultCompanyName(employee: EmployeeModel): string {
        const fullName = `${employee?.firstName || ''} ${employee?.lastName || ''}`.trim();
        if (fullName) {
            return fullName;
        }
        const emailPrefix = (employee?.email || '').split('@')[0]?.trim();
        return emailPrefix || 'New Company';
    }

    private getGeneratedOrganisationNumber(): string {
        const digits = Date.now().toString();
        return digits.slice(-10).padStart(10, '0');
    }

    onVideoLoaded() {
        this.isVideoLoaded = true;
    }

    onVideoError(event: ErrorEvent) {
        console.warn('Video loading failed:', event);
        this.isVideoLoaded = true; // Show fallback image
        if ('caches' in window) {
            caches.keys().then(cacheNames => {
                cacheNames.forEach(cacheName => {
                    caches.open(cacheName).then(cache => {
                        cache.delete('/assets/gifs/video.mp4').catch(err => {
                            console.error('Failed to delete video from cache:', err);
                        });
                    });
                });
            });
        }
    }
} 